<template>
    <TransitionRoot
        appear
        as="template"
        :show="open"
    >
        <Dialog
            as="div"
            :initial-focus="defaultFocus"
            static
            class="fixed top-0 right-0 -bottom-20 left-0 z-50 mx-auto"
            :open="open"
            @close="closeOnOutside ? closeHandler() : null"
        >
            <div
                class="mx-auto flex min-h-full max-w-5xl items-end justify-center pt-4 pb-20 text-center sm:pt-0 md:px-8"
                :class="top ? 'sm:items-start' : 'sm:items-center'"
            >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <DialogOverlay class="fixed inset-0 bg-[rgba(209,213,219,0.4)] transition-opacity" />
                </TransitionChild>
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100 translate-y-0 sm:scale-100"
                    leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        class="isolate max-h-[90vh] overflow-auto bg-white text-left shadow-xl transition-[opacity,translate,scale] sm:my-8 sm:max-w-screen"
                        :class="[
                            {
                                'rounded-t-lg sm:rounded-lg': rounded,
                                'max-w-4xl': !maxWidth,
                            },
                            modalSize,
                        ]"
                    >
                        <div class="h-full w-full md:overflow-y-auto">
                            <div class="relative">
                                <div
                                    v-if="hasHeaderSlot || title || closeButton"
                                    class="sticky top-0 z-10 flex h-16 bg-white px-6 pt-6 pb-5"
                                >
                                    <div v-show="hasHeaderSlot">
                                        <slot name="header"></slot>
                                    </div>
                                    <DialogTitle
                                        v-if="title"
                                        as="div"
                                        class="my-auto text-sm font-medium md:text-lg md:leading-5"
                                    >
                                        {{ title }}
                                    </DialogTitle>
                                    <button
                                        v-if="closeButton"
                                        id="closeButton"
                                        type="button"
                                        class="-mx-2 -my-2 my-auto ml-auto flex h-8 w-8 items-center justify-center rounded-full bg-white hover:bg-gray-100 focus:outline-hidden"
                                        @click="closeHandler"
                                    >
                                        <XMarkIcon class="h-5 w-5 text-gray-900"></XMarkIcon>
                                    </button>
                                </div>
                                <div class="font-body px-6 py-6 !pt-0 md:py-6">
                                    <slot />
                                </div>
                            </div>
                        </div>

                        <div
                            v-show="hasFooterSlot"
                            class="border-t bg-gray-50 px-4 py-3 sm:px-6"
                        >
                            <slot name="footer"></slot>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script setup>
import { XMarkIcon } from '@heroicons/vue/24/solid';
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';

const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true,
    },
    title: {
        type: String,
        required: false,
        default: '',
    },
    rounded: {
        type: Boolean,
        required: false,
        default: true,
    },
    closeButton: {
        type: Boolean,
        required: false,
        default: true,
    },
    closeOnOutside: {
        type: Boolean,
        required: false,
        default: true,
    },
    focusRef: {
        type: [Object, null],
        required: false,
        default: null,
    },
    top: {
        type: Boolean,
        default: false,
    },
    size: {
        type: String,
        required: false,
        default: 'large',
        validator: (value) => ['small', 'medium', 'payment', 'large'].includes(value),
    },
    closeViaEvent: {
        type: Boolean,
        required: false,
        default: false,
    },
    provider: {
        type: [Object, null],
        required: false,
        validator: (value) => {
            return (
                typeof value === 'object' &&
                value !== null &&
                typeof value.isVisible === 'boolean' &&
                typeof value.text === 'string' &&
                typeof value.reject === 'function' &&
                typeof value.resolve === 'function'
            );
        },
        default: null,
    },
});

const emit = defineEmits(['update:modelValue', 'close', 'cancel', 'confirm']);

const slots = useSlots();

const defaultFocus = ref(null);

async function closeHandler() {
    if (props.provider) {
        await props.provider.reject();
        open.value = false;
    } else if (props.closeViaEvent) {
        emit('close');
    } else {
        open.value = false;
    }
}

const hasHeaderSlot = computed(() => {
    return !!slots.header;
});

const hasFooterSlot = computed(() => {
    return !!slots.footer;
});

const modalSize = computed(() => {
    switch (props.size) {
        case 'small':
            return 'sm:w-1/3 w-full';
        case 'medium':
            return 'sm:w-2/3 w-full';
        case 'payment':
            return 'sm:w-[525px] w-full';
        default:
            return 'sm:w-full w-full';
    }
});

const open = computed({
    get: () => {
        if (props.provider) {
            return props.provider.isVisible.value;
        }
        return props.modelValue;
    },
    set: (value) => {
        if (props.provider) {
            if (value) {
                props.provider.resolve();
            } else {
                props.provider.reject();
            }
        } else {
            emit('update:modelValue', value);
        }
    },
});
</script>
